import { createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { getTimecardExport, getPayPeriods } from 'store/payrollExportSlice';
import {
  fetchPunchCategories,
  submitPolicy,
  updatePolicyCache,
  deletePolicy,
} from './accrualsSlice';
import {
  fetchEmployees,
  unenrollEmployees,
  enrollEmployees,
} from './accrualsEmployeesSlice';
import {
  getFinalizePayPeriods,
  getUnfinalizePayPeriods,
  setFinalizePayPeriods,
} from 'store/finalizePayPeriodSlice';

const initialState = {
  hasError: false,
  message: undefined,
  severity: undefined,
  type: undefined,
};

// set snackbar error state
const setSnackbarState = (currentState, type, message, severity = 'error') => {
  currentState.message = message;
  currentState.type = type;
  currentState.severity = severity;
};

const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState: initialState,
  reducers: {
    sendNotification(state, action) {
      try {
        state.message = action?.payload?.message;
        state.severity = action?.payload?.severity || 'success';
        state.type = 'sendNotification';
      } catch (error) {
        state.hasError = true;
        state.message = error.message;
      }
    },
    resetState(state) {
      try {
        state.message = initialState.message;
        state.severity = initialState.severity;
        state.type = initialState.type;
      } catch (error) {
        state.hasError = true;
        state.message = error.message;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPunchCategories.rejected, (state) =>
        setSnackbarState(
          state,
          'fetchPunchCategories',
          'Oops could not fetch punch categories.'
        )
      )
      .addCase(fetchEmployees.rejected, (state) =>
        setSnackbarState(
          state,
          'fetchEmployees',
          'Oops, could not fetch employees.'
        )
      )
      .addCase(unenrollEmployees.rejected, (state) =>
        setSnackbarState(
          state,
          'unenrollEmployees',
          'Oops, could not unenroll employees.'
        )
      )
      .addCase(getTimecardExport.rejected, (state) =>
        setSnackbarState(
          state,
          'getTimecardExport',
          'Oops, could not export payroll.'
        )
      )
      .addCase(getPayPeriods.rejected, (state) =>
        setSnackbarState(
          state,
          'getPayPeriods',
          'Oops, could not fetch payroll from the Timecard Summary.'
        )
      )
      .addCase(getFinalizePayPeriods.rejected, (state) =>
        setSnackbarState(
          state,
          'getFinalizePayPeriods',
          'Oops could not fetch payperiods.'
        )
      )
      .addCase(getUnfinalizePayPeriods.rejected, (state) =>
        setSnackbarState(
          state,
          'getUnfinalizePayPeriods',
          'Oops could not fetch payperiods.'
        )
      )
      .addCase(updatePolicyCache.rejected, (state) =>
        setSnackbarState(
          state,
          'updatePolicyCache',
          'Oops, Something is wrong. Please retry by going back to policies.'
        )
      )
      .addCase(setFinalizePayPeriods.rejected, (state) =>
        setSnackbarState(
          state,
          'setFinalizePayPeriods',
          'Oops could not finalize/unfinalize payperiods.'
        )
      )
      .addCase(submitPolicy.rejected, (state) =>
        setSnackbarState(
          state,
          'submitPolicy',
          'Oops, unable to save the policy.'
        )
      )
      .addCase(deletePolicy.fulfilled, (state, actions) =>
        setSnackbarState(
          state,
          'deletePolicy',
          actions?.payload?.message,
          'success'
        )
      )
      .addCase(submitPolicy.fulfilled, (state, actions) =>
        setSnackbarState(
          state,
          'submitPolicy',
          actions?.payload?.message,
          'success'
        )
      )
      .addCase(enrollEmployees.rejected, (state) =>
        setSnackbarState(
          state,
          'enrollEmployees',
          'Oops, unable to enroll employees.'
        )
      )
      .addCase(enrollEmployees.fulfilled, (state, actions) =>
        setSnackbarState(
          state,
          'enrollEmployees',
          actions?.payload?.message,
          'success'
        )
      )
      .addCase(unenrollEmployees.fulfilled, (state, actions) =>
        setSnackbarState(
          state,
          'unenrollEmployees',
          actions?.payload?.message,
          'success'
        )
      );
  },
});

export const { sendNotification, resetState } = snackbarSlice.actions;

export const snackbarSelector = R.prop('snackbar');

export default snackbarSlice.reducer;
